// Home hub — GPay-style icon grid of app sections + the most recent payment requests.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "@/lib/auth";
import { formatDate } from "@/lib/format";
import { HUB_SECTIONS } from "@/lib/nav";
import { fetchMyRequests, formatAmount, statusMeta, type PaymentRequestRow } from "@/lib/payreq";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

export default function Home() {
  return (
    <div className="flex flex-col gap-6 p-4">
      <section>
        <div className="grid grid-cols-3 gap-3">
          {HUB_SECTIONS.map((s) => (
            <Link
              key={s.href}
              to={s.href}
              className="flex flex-col items-center gap-2 rounded-2xl border border-border bg-card p-3 pt-4 text-center shadow-xs transition-transform active:scale-95"
            >
              <span
                className={cn(
                  "flex size-12 items-center justify-center rounded-full",
                  s.tileClass
                )}
              >
                <s.icon className="size-6" />
              </span>
              <span className="text-xs leading-tight font-medium">{s.label}</span>
            </Link>
          ))}
        </div>
      </section>

      <RecentRequests />
    </div>
  );
}

function RecentRequests() {
  const { token } = useAuth();
  const [rows, setRows] = useState<PaymentRequestRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    fetchMyRequests(token, { currentPage: 1, pageSize: 5 }, controller.signal)
      .then((d) => setRows(d?.rows ?? []))
      .catch(() => {
        // Non-fatal: the section simply shows its empty state.
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [token]);

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
          Recent payment requests
        </h2>
        <Link to="/requests" className="text-xs text-primary">
          View all
        </Link>
      </div>

      {loading ? (
        <Card size="sm">
          <CardContent className="flex flex-col gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between gap-2">
                <div className="flex flex-col gap-1.5">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="h-5 w-16" />
              </div>
            ))}
          </CardContent>
        </Card>
      ) : rows.length === 0 ? (
        <Card size="sm">
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Requests you raise will appear here.
          </CardContent>
        </Card>
      ) : (
        <Card size="sm" className="py-0">
          <CardContent className="divide-y divide-border p-0">
            {rows.map((r) => {
              const meta = statusMeta(r.status);
              return (
                <Link
                  key={r.id}
                  to={`/requests/${r.id}`}
                  className="flex w-full items-center gap-3 p-3 text-left transition-colors active:bg-muted/50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">
                      {r.vendorName || r.reqNote || r.receiptNumber || "(no description)"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {[r.receiptNumber, formatDate(r.reqDate)].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className="text-sm font-semibold">{formatAmount(r.amountReq)}</span>
                    <Badge variant="outline" className={cn("capitalize", meta.className)}>
                      {meta.label}
                    </Badge>
                  </div>
                </Link>
              );
            })}
          </CardContent>
        </Card>
      )}
    </section>
  );
}
